/**
 * Variant picker on the product page and in the quick add window (TROOP-861).
 *
 * Enhances Horizon's `variant-picker` without changing it:
 *
 * 1. Stock light: reads the state of every variant from the Customize app
 *    (`cheveyo-inventory-status script[data-cheveyo-variant-states]`, same section or dialog) and
 *    sets `data-ap-stock` on each option label. A value shows the best state of the variants that
 *    contain it and match the other chosen values; an open option matches everything. So a size
 *    shows its combination with the chosen colour, a colour without a chosen size the best of its
 *    sizes. The stock rule itself lives only in the app.
 *    Which values are shown depends only on the options before them, as on Trooper (TROOP-765,
 *    TROOP-844): a value disappears if no variant has it with the earlier chosen values, with the
 *    tag EOL (state „sold-out“) also if none of them can be ordered. A later option never hides an
 *    earlier value, so the colours stay put while the sizes are picked.
 * 2. Size first (product page only): without `?variant=` every option except the colour starts
 *    open. The add to cart button stays disabled with the text from the theme settings
 *    „Armypoint Produktseite“ until all options are chosen; the app shows a summary meanwhile
 *    (`cheveyo:selection-incomplete` / `cheveyo:selection-complete`).
 * 3. Colour change keeps the size if it exists in the new colour; otherwise each following
 *    option gets the nearest existing value, preferably in stock, and a short notice appears.
 *    Rule from trooper/shopify-theme (TROOP-844, assets/cheveyo-variant-preselect.js), here with
 *    the app data instead of a second variant matrix. It runs in the capture phase, before
 *    Horizon's change handler builds its request from the checked inputs.
 *
 * Horizon renders the picker again after every change (morph). The light, the legends and the
 * open options are therefore applied again once the product select event has settled.
 */

const STATES_SELECTOR = 'cheveyo-inventory-status script[data-cheveyo-variant-states]';
const PRODUCT_SELECT_EVENT = 'shopify:product:select';
const RANK = { 'in-stock': 4, delayed: 3, 'sold-out': 2, unavailable: 1 };

/**
 * @typedef {{ id: number, options: string[], available: boolean, state: string, stock: boolean }} VariantState
 * @typedef {{ complete: boolean, options: string[], colorOptionNames: string[], variants: VariantState[] }} StatesData
 * @typedef {{ chooseSize: string, open: string, sizeAdjusted: string }} PickerTexts
 * @typedef {{ main: boolean, awaiting: Set<string>, notice: string }} PickerState
 */

/** @type {WeakMap<Element, PickerState>} */
const pickers = new WeakMap();

/** @type {PickerTexts} */
const texts = readTexts();

/** @returns {PickerTexts} */
function readTexts() {
  const fallback = { chooseSize: '', open: '', sizeAdjusted: '' };
  const script = document.getElementById('ap-variant-picker-texts');
  if (!script) return fallback;
  try {
    return { ...fallback, ...JSON.parse(script.textContent || '{}') };
  } catch {
    return fallback;
  }
}

/**
 * @param {Element} picker
 * @returns {StatesData | null}
 */
function readStates(picker) {
  const scope = picker.closest('.shopify-section, dialog') || document;
  const script = scope.querySelector(STATES_SELECTOR);
  if (!script) return null;
  try {
    const data = JSON.parse(script.textContent || '');
    return data?.complete && Array.isArray(data.variants) && Array.isArray(data.options) ? data : null;
  } catch {
    return null;
  }
}

/** @param {Element} picker */
function isMainPicker(picker) {
  return Boolean(picker.closest('.product-information')) && !picker.closest('.quick-add-modal__content, dialog');
}

/**
 * Option names that count as colour, from the app setting „Optionsname Farbe“. Without app data
 * (block missing, over 250 variants) the first of several options counts as colour, so the page
 * still starts with a colour and only the sizes open.
 * @param {StatesData | null} data
 * @param {Element} picker
 */
function colorNames(data, picker) {
  const names = (data?.colorOptionNames || []).map((name) => name.trim()).filter(Boolean);
  if (data) return names;
  const fieldsets = fieldsetsOf(picker);
  return fieldsets.length > 1 && fieldsets[0] ? [fieldsets[0].name] : [];
}

/**
 * Fieldsets of the picker with their option name.
 * @param {Element} picker
 * @returns {{ fieldset: HTMLFieldSetElement, name: string, inputs: HTMLInputElement[] }[]}
 */
function fieldsetsOf(picker) {
  return Array.from(picker.querySelectorAll('fieldset.variant-option'), (fieldset) => {
    const inputs = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
    return { fieldset: /** @type {HTMLFieldSetElement} */ (fieldset), name: inputs[0]?.dataset.optionName || '', inputs };
  }).filter((entry) => entry.name);
}

/**
 * Chosen value per product option, `null` for open options and options without a fieldset.
 * @param {Element} picker
 * @param {string[]} optionNames
 * @param {Set<string>} [awaiting]
 */
function selectionOf(picker, optionNames, awaiting = new Set()) {
  return optionNames.map((name) => {
    if (awaiting.has(name)) return null;
    const checked = Array.from(picker.querySelectorAll('fieldset input[type="radio"]:checked')).find(
      (input) => input instanceof HTMLInputElement && input.dataset.optionName === name
    );
    return checked instanceof HTMLInputElement ? checked.value : null;
  });
}

/**
 * Best state of the variants with `value` at `index` that match the other chosen values.
 * @param {StatesData} data
 * @param {(string | null)[]} selection
 * @param {number} index
 * @param {string} value
 */
function stateFor(data, selection, index, value) {
  const candidates = data.variants.filter(
    (variant) =>
      variant.options[index] === value &&
      selection.every((chosen, other) => other === index || chosen == null || variant.options[other] === chosen)
  );
  if (!candidates.length) return 'none';
  let best = '';
  for (const { state } of candidates) {
    if (state && (RANK[/** @type {keyof RANK} */ (state)] || 0) > (RANK[/** @type {keyof RANK} */ (best)] || 0)) best = state;
  }
  return best;
}

/**
 * Whether `value` at `index` is offered after the values chosen in the earlier options, like
 * Shopify's product_option_value.available. Variants that are „sold-out“ (EOL) do not count.
 * @param {StatesData} data
 * @param {(string | null)[]} selection
 * @param {number} index
 * @param {string} value
 */
function isOffered(data, selection, index, value) {
  return data.variants.some(
    (variant) =>
      variant.options[index] === value &&
      variant.state !== 'sold-out' &&
      selection.every((chosen, other) => other >= index || chosen == null || variant.options[other] === chosen)
  );
}

/**
 * Applies light, hidden values, legends and open options to the current DOM of the picker.
 * @param {Element} picker
 */
function render(picker) {
  const state = pickers.get(picker);
  if (!state) return;
  const data = readStates(picker);
  const fieldsets = fieldsetsOf(picker);
  const colors = colorNames(data, picker);
  const optionNames = data?.options || fieldsets.map((entry) => entry.name);

  // Open options: nothing checked, so neither Horizon nor the light treat a value as chosen
  for (const { name, inputs } of fieldsets) {
    if (!state.awaiting.has(name)) continue;
    for (const input of inputs) input.checked = false;
  }

  const selection = selectionOf(picker, optionNames, state.awaiting);

  for (const { fieldset, name, inputs } of fieldsets) {
    setAttribute(fieldset, 'data-ap-option', colors.includes(name) ? 'color' : 'size');
    setAttribute(fieldset, 'data-ap-awaiting', state.awaiting.has(name) ? '' : null);
    renderLegend(fieldset, name, selection[optionNames.indexOf(name)] ?? null, state.awaiting.has(name));

    const index = optionNames.indexOf(name);
    for (const input of inputs) {
      const label = input.closest('label');
      if (!label) continue;
      const stock = data && index >= 0 ? stateFor(data, selection, index, input.value) : '';
      setAttribute(label, 'data-ap-stock', stock || null);
      const offered = !data || index < 0 || input.checked || isOffered(data, selection, index, input.value);
      setAttribute(label, 'data-ap-hidden', offered ? null : '');
    }
  }
}

/**
 * Changes an attribute only if needed, so the observer below does not see its own writes.
 * @param {Element} element
 * @param {string} name
 * @param {string | null} value - `null` removes the attribute
 */
function setAttribute(element, name, value) {
  if (element.getAttribute(name) === value) return;
  if (value === null) element.removeAttribute(name);
  else element.setAttribute(name, value);
}

/**
 * „Größe: M“ or „Größe: Bitte wählen“ as in the prototype.
 * @param {HTMLFieldSetElement} fieldset
 * @param {string} name
 * @param {string | null} value
 * @param {boolean} open
 */
function renderLegend(fieldset, name, value, open) {
  const legend = fieldset.querySelector('legend');
  if (!legend) return;
  const label = legend.firstChild;
  if (label?.nodeType === Node.TEXT_NODE && !label.textContent?.trim().endsWith(':')) {
    label.textContent = `${label.textContent?.trim()}:`;
  }
  // Swatches already show the chosen value (Horizon .variant-option__swatch-value)
  if (legend.querySelector('.variant-option__swatch-value')) return;

  let output = legend.querySelector('.ap-variant-option__value');
  const text = open ? texts.open : value || '';
  if (!text) {
    output?.remove();
    return;
  }
  if (!output) {
    output = document.createElement('span');
    output.className = 'ap-variant-option__value';
    legend.append(output);
  }
  if (output.textContent !== text) output.textContent = text;
  if (output.classList.contains('ap-variant-option__value--open') !== open) {
    output.classList.toggle('ap-variant-option__value--open', open);
  }
}

/**
 * Product page: disables add to cart while an option is open and tells the app.
 * @param {Element} picker
 */
function applyAwaiting(picker) {
  const state = pickers.get(picker);
  if (!state?.main) return;
  const section = picker.closest('.shopify-section');
  const information = picker.closest('.product-information');
  const open = state.awaiting.size > 0;
  information?.toggleAttribute('data-ap-awaiting', open);

  const data = readStates(picker);
  const optionNames = data?.options || fieldsetsOf(picker).map((entry) => entry.name);
  const options = selectionOf(picker, optionNames, state.awaiting);
  picker.dispatchEvent(
    new CustomEvent(open ? 'cheveyo:selection-incomplete' : 'cheveyo:selection-complete', {
      bubbles: true,
      detail: { options },
    })
  );

  if (!open) return;
  const button = section?.querySelector('product-form-component [ref="addToCartButton"]');
  if (!(button instanceof HTMLButtonElement)) return;
  button.disabled = true;
  if (texts.chooseSize) {
    const label = Array.from(button.childNodes).find((node) => node.nodeType === Node.TEXT_NODE && node.textContent?.trim());
    if (label) label.textContent = texts.chooseSize;
  }
}

/** @param {Element} picker */
function enhance(picker) {
  if (pickers.has(picker)) return;
  const main = isMainPicker(picker);
  const data = readStates(picker);
  const colors = colorNames(data, picker);

  /** @type {Set<string>} */
  const awaiting = new Set();
  if (main && !new URL(window.location.href).searchParams.has('variant')) {
    for (const { name, inputs } of fieldsetsOf(picker)) {
      if (!colors.includes(name) && inputs.length > 1) awaiting.add(name);
    }
  }

  pickers.set(picker, { main, awaiting, notice: '' });
  noticeOf(picker);
  render(picker);

  // Only one value left to choose (others hidden as gone): nothing to decide, keep it chosen
  let single = false;
  for (const { name, inputs } of fieldsetsOf(picker)) {
    if (!awaiting.has(name)) continue;
    const visible = inputs.filter((input) => !input.closest('label')?.hasAttribute('data-ap-hidden'));
    if (visible.length !== 1 || !visible[0]) continue;
    awaiting.delete(name);
    visible[0].checked = true;
    single = true;
  }
  if (single) render(picker);

  applyAwaiting(picker);
  // The app's script may load after this one; tell its status again once it listens
  if (main && !customElements.get('cheveyo-inventory-status')) {
    customElements.whenDefined('cheveyo-inventory-status').then(() => applyAwaiting(picker));
  }
}

/**
 * Capture phase, before Horizon: marks a chosen option and applies the TROOP-844 rule.
 * @param {Event} event
 */
function handleChange(event) {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
  const picker = input.closest('variant-picker');
  const state = picker && pickers.get(picker);
  if (!picker || !state) return;

  const notice = noticeOf(picker);
  if (notice) notice.textContent = '';
  state.notice = '';

  const name = input.dataset.optionName || '';
  state.awaiting.delete(name);
  picker.querySelector(`fieldset[data-ap-awaiting] input[data-option-name="${CSS.escape(name)}"]`)
    ?.closest('fieldset')
    ?.removeAttribute('data-ap-awaiting');

  const data = readStates(picker);
  if (!data) return;
  const index = data.options.indexOf(name);
  if (index < 0) return;

  const selection = selectionOf(picker, data.options, state.awaiting);
  selection[index] = input.value;
  if (selection.some((value) => value == null)) return;

  // Grey with EOL means „gone“; such variants do not count as existing
  const variants = data.variants.filter((variant) => variant.state !== 'sold-out');
  const exists = variants.some((variant) => variant.options.every((value, i) => value === selection[i]));
  if (exists) return;

  let candidates = variants.filter((variant) => variant.options.slice(0, index + 1).every((value, i) => value === selection[i]));
  if (!candidates.length) return;

  let adjusted = false;
  for (let next = index + 1; next < data.options.length; next++) {
    const value = pickValue(candidates, selection[next], next);
    if (value == null) return;
    if (value !== selection[next]) {
      adjusted = true;
      const target = Array.from(picker.querySelectorAll(`input[data-option-name="${CSS.escape(data.options[next])}"]`)).find(
        (radio) => radio instanceof HTMLInputElement && radio.value === value
      );
      if (!(target instanceof HTMLInputElement)) return;
      target.checked = true;
    }
    selection[next] = value;
    candidates = candidates.filter((variant) => variant.options[next] === value);
  }
  if (adjusted) state.notice = texts.sizeAdjusted;
}

/**
 * The wanted value if it exists, otherwise the numerically nearest, preferably available.
 * From trooper/shopify-theme assets/cheveyo-variant-preselect.js (TROOP-844).
 * @param {VariantState[]} candidates
 * @param {string | null} wanted
 * @param {number} position
 * @returns {string | null}
 */
function pickValue(candidates, wanted, position) {
  /** @type {string[]} */
  const values = [];
  for (const variant of candidates) {
    const value = variant.options[position];
    if (value != null && !values.includes(value)) values.push(value);
  }
  if (wanted && values.includes(wanted)) return wanted;

  const available = values.filter((value) => candidates.some((variant) => variant.options[position] === value && variant.available));
  const pool = available.length ? available : values;

  const wantedNumber = toNumber(wanted);
  if (wantedNumber === null) return pool[0] ?? null;

  let best = null;
  for (const value of pool) {
    const number = toNumber(value);
    if (number === null) continue;
    if (best === null) {
      best = value;
      continue;
    }
    const distance = Math.abs(number - wantedNumber);
    const bestNumber = /** @type {number} */ (toNumber(best));
    const bestDistance = Math.abs(bestNumber - wantedNumber);
    // Same distance: the smaller number keeps the result stable
    if (distance < bestDistance || (distance === bestDistance && number < bestNumber)) best = value;
  }
  return best ?? pool[0] ?? null;
}

/** @param {string | null} value */
function toNumber(value) {
  const number = parseFloat(String(value).replace(',', '.'));
  return Number.isNaN(number) ? null : number;
}

/**
 * After Horizon has rendered the new picker and the buy buttons: apply everything again.
 * @param {Event & { promise?: Promise<unknown> }} event
 */
function handleProductSelect(event) {
  const picker = event.target instanceof Element ? event.target.closest('variant-picker') : null;
  if (!picker || !pickers.has(picker) || !event.promise) return;
  event.promise
    .then((/** @type {any} */ result) => {
      render(picker);
      applyAwaiting(picker);
      showNotice(picker);
      syncVariantUrl(picker, result?.detail?.resource?.id);
    })
    .catch(() => {});
}

/**
 * Horizon writes `?variant=` from the clicked value; after the TROOP-844 rule moved the size that
 * is not the variant shown. The address follows the variant Shopify returned, so a reload keeps it.
 * Horizon's own update runs earlier (right after the click), so this one wins.
 * @param {Element} picker
 * @param {number | string | undefined} variantId
 */
function syncVariantUrl(picker, variantId) {
  const state = pickers.get(picker);
  if (!state?.main) return;
  const url = new URL(window.location.href);
  // While the size is open, no variant is chosen yet; a reload starts open again
  const wanted = state.awaiting.size || !variantId ? null : String(variantId);
  if (url.searchParams.get('variant') === wanted) return;
  if (wanted) url.searchParams.set('variant', wanted);
  else url.searchParams.delete('variant');
  history.replaceState(history.state, '', url.toString());
}

/**
 * One lasting, empty live region after the picker; screen readers only announce text that
 * changes inside a region they already know.
 * @param {Element} picker
 * @returns {HTMLElement | null}
 */
function noticeOf(picker) {
  const next = picker.nextElementSibling;
  if (next instanceof HTMLElement && next.classList.contains('ap-variant-notice')) return next;
  if (!picker.parentElement) return null;
  const notice = document.createElement('p');
  notice.className = 'ap-variant-notice';
  notice.setAttribute('role', 'status');
  picker.after(notice);
  return notice;
}

/** @param {Element} picker */
function showNotice(picker) {
  const state = pickers.get(picker);
  if (!state?.notice) return;
  const notice = noticeOf(picker);
  if (notice) notice.textContent = state.notice;
  state.notice = '';
}

/** @type {Set<Element>} */
const pending = new Set();

/**
 * Pickers that appear later (quick add window) and pickers Horizon morphs without a product
 * select event: the quick add window morphs a new product into the existing picker and drops
 * the attributes set here. Changes inside a known picker render it again in the next frame.
 */
const observer = new MutationObserver((records) => {
  for (const record of records) {
    for (const node of record.addedNodes) {
      if (!(node instanceof Element)) continue;
      if (node.matches('variant-picker')) enhance(node);
      node.querySelectorAll('variant-picker').forEach(enhance);
    }
    const target = record.target instanceof Element ? record.target : record.target.parentElement;
    const picker = target?.closest('variant-picker');
    if (picker && pickers.has(picker)) pending.add(picker);
  }
  if (!pending.size) return;
  requestAnimationFrame(() => {
    for (const picker of pending) if (picker.isConnected) render(picker);
    pending.clear();
  });
});

customElements.whenDefined('variant-picker').then(() => {
  document.querySelectorAll('.product-details variant-picker').forEach(enhance);
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['data-ap-option', 'data-ap-stock', 'data-ap-hidden', 'data-ap-awaiting', 'checked'],
  });
  document.addEventListener('change', handleChange, true);
  document.addEventListener(PRODUCT_SELECT_EVENT, handleProductSelect);
});
